// Fallback cover poster for events without a cover image.
import { getEventType } from "@shared/eventTypes";

interface EventPosterProps {
  name: string;
  label: string;
  types: string[];
  className?: string;
}

type GradientVariant = {
  cardGradient: string;
  labelColor: string;
};

// Title divider style — a different motif per event-type group.
type DividerKind = "wave" | "line" | "dashes" | "dots" | "zigzag" | "double";

type Theme = {
  folder: "NIGHTLIFE" | "CULTURE" | "SPORTS" | "ACTIVITIES";
  divider: DividerKind;
  // CSS font-family for the event title — a different display face per group.
  titleFont: string;
  variants: GradientVariant[];
};

// Filenames per folder — kept in sync with /public/img/events/{FOLDER}/
const FOLDER_IMAGES = {
  NIGHTLIFE: [
    "087203bf0ec98c8418950a9e6c2eb566",
    "3e1864146bbabaa0acacfdd7d782da1f",
    "43190e579ab645feeb87b03d2839dc90",
    "54bcc0f6ad1135afb572e985ea0eaf4d",
    "8ab311d89049862494dec740835d825b",
    "8c8cbd7c6e4f0d1221fd7f951f4efe4b",
    "b5cd0d974bea739be9c2da72975573b0",
    "be8d29e0c760841cbcb5f7d562158c3d",
    "c570ca885b1d983ae6579e6ab4466616",
    "f8885120e7f81c6a228142ef67d58c6f",
    "c8a8dee2303bea98d7d4f38c9898c3c3",
    "ab5eb46139b06101cecaae0102d024d9",
    "89e72ae86fd6d55b7456c86a611fe6a1",
  ],
  CULTURE: [
    "282fa7393fc82f593ed256049a855ab4",
    "34b2ca578971f62806278e8128021669",
    "3ffe6f434ea87950010e8ea3becfde03",
    "6f9765a258c8a23cc449a2644a756a12",
    "a0559b2f9d153404c84bc3ba870ad112",
    "a156a0b014e35b9cb1ec0b1af0a78d69",
    "daa0499a38e5049332062f18e0635352",
    "e80b8e7942a117718bf0ca5497bc40c0",
    "a8a9ccc1b2ca5ee15cf3852031ce15c2",
    "02de0d67eb8770b3cf3a0ae7e47a371a",
    "a521ca2ec616fafbd5265839adae137f",
    "d5defd0daa3ed748d0ccfcf5676e6751",
  ],
  SPORTS: [
    "21758a5ab54fec022fa9e86595f58a5b",
    "36879476c6cd3c4b3a9345a9bacb0a7b",
    "778462bbcad9468f32fa7fc567f9e373",
    "8407c2a3021345eaccf8f0e1d30ba170",
    "92a87de22c746c30dfa83cb11b913612",
    "93dde38db020514f47b74327dc2c767b",
    "a38666707ad14d6e63eda0913775c900",
    "b6a9680b0e74d44e51f4981b4bf2637f",
    "e894a559f19e7ad87bc3ad318d5e8b39",
    "ec4d3c93a9286407fc7ac581bdf064ea",
    "fe026602b78a44333d41f2d8a468e94f",
    "2c5850b539e99872139480d466c28af6",
    "22bdf9557a93cc72dde42f39e7e9c02b",
    "e122f36c7f9985c42dadd8fbbad780e4",
    "ba74213e7fac5eaa47352efb71c5a3ed",
  ],
  ACTIVITIES: [
    "02c60fa4ac268fd18bcda6a016ffc171",
    "592cb989eccf53c4e734b9bbdd3b4ed6",
    "6380546ed594c120f59420891c8cdd55",
    "95219fcaa69a334c09be2525b530b99b",
    "9e9bb639ef9c85ec1a2c082bfc22fb1a",
    "bbb60b4c360da6203562b46f54b63282",
    "e9678e119ab35fa513e85666203f4973",
    "141fd2d2fab23c36e5924bf4651f3a14",
    "0ce3cc8996507a340d98dd0ce78b8a4c",
    "1c4307eaf6ca131af950e5038fe6b226",
    "b18f01a366bb0f72f62689bca31774b6",
  ],
} satisfies Record<string, string[]>;

// 4 visually distinct gradient variants per group, selected by name hash.
const THEMES: Record<string, Theme> = {
  nightlife: {
    folder: "NIGHTLIFE",
    divider: "zigzag",
    titleFont: "'Bebas Neue', Impact, sans-serif",
    variants: [
      { cardGradient: "linear-gradient(155deg, rgba(109,40,217,0.85) 0%, rgba(46,16,101,0.95) 100%)", labelColor: "#C4B5FD" },
      { cardGradient: "linear-gradient(155deg, rgba(30,58,138,0.85) 0%, rgba(15,23,42,0.95) 100%)",  labelColor: "#93C5FD" },
      { cardGradient: "linear-gradient(155deg, rgba(157,23,77,0.85) 0%, rgba(76,5,35,0.95) 100%)",   labelColor: "#F9A8D4" },
      { cardGradient: "linear-gradient(155deg, rgba(17,78,74,0.85) 0%, rgba(4,47,46,0.95) 100%)",    labelColor: "#5EEAD4" },
    ],
  },
  community: {
    folder: "NIGHTLIFE",
    divider: "dots",
    titleFont: "'Space Grotesk', system-ui, sans-serif",
    variants: [
      { cardGradient: "linear-gradient(155deg, rgba(162,28,175,0.85) 0%, rgba(74,16,80,0.95) 100%)",  labelColor: "#F0ABFC" },
      { cardGradient: "linear-gradient(155deg, rgba(136,19,55,0.85) 0%, rgba(64,7,22,0.95) 100%)",    labelColor: "#FDA4AF" },
      { cardGradient: "linear-gradient(155deg, rgba(55,48,163,0.85) 0%, rgba(26,21,90,0.95) 100%)",   labelColor: "#C7D2FE" },
      { cardGradient: "linear-gradient(155deg, rgba(109,40,217,0.85) 0%, rgba(46,16,101,0.95) 100%)", labelColor: "#C4B5FD" },
    ],
  },
  arts: {
    folder: "CULTURE",
    divider: "wave",
    titleFont: "'Abril Fatface', Georgia, serif",
    variants: [
      { cardGradient: "linear-gradient(155deg, rgba(26,107,101,0.85) 0%, rgba(8,46,43,0.95) 100%)",   labelColor: "#99F6E4" },
      { cardGradient: "linear-gradient(155deg, rgba(120,53,15,0.85) 0%, rgba(67,20,7,0.95) 100%)",    labelColor: "#FED7AA" },
      { cardGradient: "linear-gradient(155deg, rgba(49,46,129,0.85) 0%, rgba(19,18,60,0.95) 100%)",   labelColor: "#C7D2FE" },
      { cardGradient: "linear-gradient(155deg, rgba(157,23,77,0.85) 0%, rgba(76,5,35,0.95) 100%)",    labelColor: "#F9A8D4" },
    ],
  },
  attire: {
    folder: "CULTURE",
    divider: "dashes",
    titleFont: "'Playfair Display', Georgia, serif",
    variants: [
      { cardGradient: "linear-gradient(155deg, rgba(44,62,85,0.85) 0%, rgba(15,26,40,0.95) 100%)",    labelColor: "#CBD5E1" },
      { cardGradient: "linear-gradient(155deg, rgba(88,28,135,0.85) 0%, rgba(46,16,101,0.95) 100%)",  labelColor: "#E9D5FF" },
      { cardGradient: "linear-gradient(155deg, rgba(26,107,101,0.85) 0%, rgba(8,46,43,0.95) 100%)",   labelColor: "#99F6E4" },
      { cardGradient: "linear-gradient(155deg, rgba(120,53,15,0.85) 0%, rgba(67,20,7,0.95) 100%)",    labelColor: "#FED7AA" },
    ],
  },
  venue: {
    folder: "CULTURE",
    divider: "line",
    titleFont: "'Archivo Black', system-ui, sans-serif",
    variants: [
      { cardGradient: "linear-gradient(155deg, rgba(55,48,46,0.85) 0%, rgba(24,21,20,0.95) 100%)",    labelColor: "#D6D3D1" },
      { cardGradient: "linear-gradient(155deg, rgba(15,23,42,0.85) 0%, rgba(2,6,23,0.95) 100%)",      labelColor: "#93C5FD" },
      { cardGradient: "linear-gradient(155deg, rgba(74,16,80,0.85) 0%, rgba(38,5,42,0.95) 100%)",     labelColor: "#F0ABFC" },
      { cardGradient: "linear-gradient(155deg, rgba(17,78,74,0.85) 0%, rgba(4,47,46,0.95) 100%)",     labelColor: "#5EEAD4" },
    ],
  },
  outdoors: {
    folder: "SPORTS",
    divider: "zigzag",
    titleFont: "'Bebas Neue', Impact, sans-serif",
    variants: [
      { cardGradient: "linear-gradient(155deg, rgba(26,112,64,0.85) 0%, rgba(10,51,32,0.95) 100%)",   labelColor: "#86EFAC" },
      { cardGradient: "linear-gradient(155deg, rgba(12,74,110,0.85) 0%, rgba(7,36,55,0.95) 100%)",    labelColor: "#7DD3FC" },
      { cardGradient: "linear-gradient(155deg, rgba(120,53,15,0.85) 0%, rgba(60,25,5,0.95) 100%)",    labelColor: "#FDBA74" },
      { cardGradient: "linear-gradient(155deg, rgba(88,28,135,0.85) 0%, rgba(46,16,101,0.95) 100%)",  labelColor: "#E9D5FF" },
    ],
  },
  wellness: {
    folder: "SPORTS",
    divider: "wave",
    titleFont: "'Caveat', 'Comic Sans MS', cursive",
    variants: [
      { cardGradient: "linear-gradient(155deg, rgba(8,122,92,0.85) 0%, rgba(3,56,40,0.95) 100%)",     labelColor: "#6EE7B7" },
      { cardGradient: "linear-gradient(155deg, rgba(12,74,110,0.85) 0%, rgba(7,36,55,0.95) 100%)",    labelColor: "#7DD3FC" },
      { cardGradient: "linear-gradient(155deg, rgba(30,41,59,0.85) 0%, rgba(15,23,42,0.95) 100%)",    labelColor: "#CBD5E1" },
      { cardGradient: "linear-gradient(155deg, rgba(26,112,64,0.85) 0%, rgba(10,51,32,0.95) 100%)",   labelColor: "#86EFAC" },
    ],
  },
  food: {
    folder: "ACTIVITIES",
    divider: "dots",
    titleFont: "'Pacifico', cursive",
    variants: [
      { cardGradient: "linear-gradient(155deg, rgba(185,28,28,0.85) 0%, rgba(107,18,18,0.95) 100%)",  labelColor: "#FCA5A5" },
      { cardGradient: "linear-gradient(155deg, rgba(154,52,18,0.85) 0%, rgba(94,27,8,0.95) 100%)",    labelColor: "#FDBA74" },
      { cardGradient: "linear-gradient(155deg, rgba(109,40,217,0.85) 0%, rgba(46,16,101,0.95) 100%)", labelColor: "#C4B5FD" },
      { cardGradient: "linear-gradient(155deg, rgba(17,78,74,0.85) 0%, rgba(4,47,46,0.95) 100%)",     labelColor: "#5EEAD4" },
    ],
  },
  games: {
    folder: "ACTIVITIES",
    divider: "double",
    titleFont: "'Space Grotesk', system-ui, sans-serif",
    variants: [
      { cardGradient: "linear-gradient(155deg, rgba(40,81,196,0.85) 0%, rgba(17,34,96,0.95) 100%)",   labelColor: "#93C5FD" },
      { cardGradient: "linear-gradient(155deg, rgba(109,40,217,0.85) 0%, rgba(46,16,101,0.95) 100%)", labelColor: "#C4B5FD" },
      { cardGradient: "linear-gradient(155deg, rgba(26,112,64,0.85) 0%, rgba(10,51,32,0.95) 100%)",   labelColor: "#86EFAC" },
      { cardGradient: "linear-gradient(155deg, rgba(157,23,77,0.85) 0%, rgba(76,5,35,0.95) 100%)",    labelColor: "#F9A8D4" },
    ],
  },
  social: {
    folder: "ACTIVITIES",
    divider: "dashes",
    titleFont: "'Pacifico', cursive",
    variants: [
      { cardGradient: "linear-gradient(155deg, rgba(168,60,25,0.85) 0%, rgba(85,30,12,0.95) 100%)",   labelColor: "#FED7AA" },
      { cardGradient: "linear-gradient(155deg, rgba(157,23,77,0.85) 0%, rgba(76,5,35,0.95) 100%)",    labelColor: "#F9A8D4" },
      { cardGradient: "linear-gradient(155deg, rgba(180,83,9,0.85) 0%, rgba(92,45,5,0.95) 100%)",     labelColor: "#FDE68A" },
      { cardGradient: "linear-gradient(155deg, rgba(30,58,138,0.85) 0%, rgba(15,23,42,0.95) 100%)",   labelColor: "#93C5FD" },
    ],
  },
};

const DEFAULT_THEME: Theme = {
  folder: "NIGHTLIFE",
  divider: "wave",
  titleFont: "'Playfair Display', Georgia, serif",
  variants: [
    { cardGradient: "linear-gradient(155deg, rgba(162,28,175,0.85) 0%, rgba(74,16,80,0.95) 100%)", labelColor: "#F0ABFC" },
    { cardGradient: "linear-gradient(155deg, rgba(30,58,138,0.85) 0%, rgba(15,23,42,0.95) 100%)",  labelColor: "#93C5FD" },
    { cardGradient: "linear-gradient(155deg, rgba(17,78,74,0.85) 0%, rgba(4,47,46,0.95) 100%)",    labelColor: "#5EEAD4" },
    { cardGradient: "linear-gradient(155deg, rgba(88,28,135,0.85) 0%, rgba(46,16,101,0.95) 100%)", labelColor: "#E9D5FF" },
  ],
};

// ---------------------------------------------------------------------------

function nameHash(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(h);
}

// Identity/community tags (queer, trans, sapphic…) describe *who* an event is
// for, not *what* it is, so they make a poor basis for the visual theme. When an
// event also carries an activity/venue tag we prefer that; community is only a
// fallback when it's the sole recognizable group.
const DEPRIORITIZED_GROUPS = new Set(["community"]);

function resolveTheme(
  types: string[],
  name: string,
): { folder: keyof typeof FOLDER_IMAGES; cardGradient: string; labelColor: string; divider: DividerKind; titleFont: string } {
  const h = nameHash(name);
  const matched = (predicate: (group: string) => boolean): Theme | undefined => {
    for (const slug of types) {
      const et = getEventType(slug);
      if (et && THEMES[et.group] && predicate(et.group)) return THEMES[et.group];
    }
    return undefined;
  };
  // First pass prefers a non-deprioritized group; fall back to any group.
  const theme = matched((g) => !DEPRIORITIZED_GROUPS.has(g)) ?? matched(() => true);
  if (theme) {
    const v = theme.variants[h % theme.variants.length];
    return { folder: theme.folder, cardGradient: v.cardGradient, labelColor: v.labelColor, divider: theme.divider, titleFont: theme.titleFont };
  }
  const v = DEFAULT_THEME.variants[h % DEFAULT_THEME.variants.length];
  return { folder: DEFAULT_THEME.folder, cardGradient: v.cardGradient, labelColor: v.labelColor, divider: DEFAULT_THEME.divider, titleFont: DEFAULT_THEME.titleFont };
}

function pickImage(folder: keyof typeof FOLDER_IMAGES, name: string): string {
  const files = FOLDER_IMAGES[folder];
  // Different multiplier from resolveTheme so image and gradient vary independently.
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 37 + name.charCodeAt(i)) | 0;
  return `/img/events/${folder}/${files[Math.abs(h) % files.length]}.webp`;
}

// Renders the divider motif chosen for the event's theme. All variants share a
// 120×10 viewBox so they line up under the title at the same size.
function TitleDivider({ kind, color }: { kind: DividerKind; color: string }) {
  return (
    <svg
      className="w-[40cqi] max-w-[180px] h-auto"
      viewBox="0 0 120 10"
      fill="none"
      preserveAspectRatio="none"
      aria-hidden
    >
      {kind === "wave" && (
        <path
          d="M2 6 Q 12 1, 22 6 T 42 6 T 62 6 T 82 6 T 102 6 T 118 6"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      )}
      {kind === "line" && (
        <path d="M2 6 H118" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      )}
      {kind === "dashes" && (
        <path
          d="M2 6 H118"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray="9 7"
        />
      )}
      {kind === "dots" && (
        <path
          d="M3 6 H117"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="0.1 11"
        />
      )}
      {kind === "zigzag" && (
        <path
          d="M2 8 L 17 2 L 32 8 L 47 2 L 62 8 L 77 2 L 92 8 L 107 2 L 118 7"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {kind === "double" && (
        <>
          <path d="M2 4 H118" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
          <path d="M2 8 H118" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

export function EventPoster({ name, label, types, className }: EventPosterProps) {
  const { folder, labelColor, divider, titleFont } = resolveTheme(types, name);
  const bgSrc = pickImage(folder, name);
  // Deterministic tilt to the title card — varies per event by name hash.
  const tiltDeg = (nameHash(name) % 21) - 10; // -10..10°

  return (
    <div className={`relative w-full h-full [container-type:inline-size] overflow-hidden ${className ?? ""}`}>
      {/* Blurred background photo */}
      <img
        src={bgSrc}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover scale-100 group-hover:scale-105 transition-transform duration-500"
        style={{ filter: "blur(2px)" }}
      />

        <p
          className="absolute top-2 text-center left-0 right-0 text-[9px] font-bold uppercase tracking-[0.18em] text-center"
          style={{ color: labelColor }}
        >
          {label}
        </p>

      {/* Colored card overlay — slightly inset so the blurred photo peeks around
          edges, and tilted a touch (deterministic per title) for character. */}
      <div
        className="absolute top-[20%] -left-[6cqi] -right-[6cqi] flex flex-col gap-2 py-4 px-[12cqi] overflow-hidden backdrop-blur-xl bg-black/30"
        style={{ transform: `rotate(${tiltDeg}deg)` }}
      >

        <div className="flex-1 flex flex-col items-center justify-center gap-2">

          <h2
            className="text-white text-center font-bold  leading-[1.1] break-words"
            style={{
              fontFamily: titleFont,
              fontSize: "clamp(1.1rem, 9cqi, 3.5rem)",
            }}
          >
            {name}
          </h2>

          {/* Title divider — motif varies by event type */}
          <TitleDivider kind={divider} color={labelColor} />
        </div>
      </div>
    </div>
  );
}
